/**
=========================================================
* Material Dashboard 2 PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 PRO React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";

// Material Dashboard 2 PRO React routes
import routes from "routes";

// Material Dashboard 2 PRO React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
} from "context";

// Images
import logoViacredi from "assets/images/logo-viacredi.png";
import logoMiniViacredi from "assets/images/mini-viacredi.png";

import { TableDataProvider } from "context/TableDataContext";
import { CrudCooperatedProvider } from "context/CrudCooperatedContext";
import { CrudCompanyProvider } from "context/CrudCompanyContext";
import { CrudUserProvider } from "context/CrudUserContext";
import { useAuthContext } from "context/AuthContext";
import { DashboardCardProvider } from "context/DashboardCardContext";
import { SocketProvider } from "context/SocketContext";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const { authenticated } = useAuthContext();

  // Cache for the rtl
  useMemo(() => {
    createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      return null;
    });

  return (
    <ThemeProvider theme={darkMode ? theme : theme}>
      <TableDataProvider>
        <SocketProvider>
          <CrudCompanyProvider>
            <CrudCooperatedProvider>
              <CrudUserProvider>
                <DashboardCardProvider>
                  <CssBaseline />
                  {authenticated && layout === "dashboard" && (
                    <Sidenav
                      color={sidenavColor}
                      brand={miniSidenav ? logoMiniViacredi : logoViacredi}
                      brandName=""
                      routes={routes}
                      onMouseEnter={handleOnMouseEnter}
                      onMouseLeave={handleOnMouseLeave}
                    />
                  )}
                  {authenticated && layout === "vr" && <Configurator />}
                  <Routes>
                    {getRoutes(routes)}
                    <Route path="*" element={<Navigate to="/auth/login" />} />
                  </Routes>
                </DashboardCardProvider>
              </CrudUserProvider>
            </CrudCooperatedProvider>
          </CrudCompanyProvider>
        </SocketProvider>
      </TableDataProvider>
    </ThemeProvider>
  );
}
